<script lang="ts">
    import AttributeAnswers from "@apps/reviews/components/AttributeAnswers/AttributeAnswers.svelte";
    import ReviewRatingStars from "@apps/reviews/components/RatingStars/ReviewRatingStars.svelte";
    import ReviewImageList from "@apps/reviews/components/ReviewImageList/ReviewImageList.svelte";
    import ReviewsEditButton from "@apps/reviews/components/ReviewsEditButton.svelte";
    import ImportedReview from "@apps/reviews/components/ReviewsMeta/ImportedReview.svelte";
    import Incentivized from "@apps/reviews/components/ReviewsMeta/IncentivizedReview.svelte";
    import VerifiedBuyer from "@apps/reviews/components/ReviewsMeta/VerifiedBuyer.svelte";
    import ReviewsTranslateButton from "@apps/reviews/components/ReviewsTranslateButton.svelte";
    import ToggleVote from "@apps/reviews/components/ToggleVote.svelte";
    import { REVIEWS_REPLY_STATUS } from "@apps/reviews/constants";
    import ReviewEntity from "@apps/reviews/entities/ReviewEntity";
    import { VotesState } from "@apps/reviews/stores/VotesStore";
    import { getHumanReadableDate } from "@lib/utils";

    import Badge from "@components/Badge/Badge.svelte";
    import ClockIcon from "@components/icons/ClockIcon.svelte";
    import T from "@components/T.svelte";
    import ViewMore from "@components/ViewMore.svelte";

    import ReviewsProduct from "../../ReviewsProduct.svelte";
    import ReviewsUnavailableProduct from "../../ReviewsUnavailableProduct.svelte";
    import ReviewReply from "../ReviewReply/ReviewReply.svelte";
    import ReviewItemFrame from "./ReviewItemFrame.svelte";

    export let namespace: string;
    export let review: ReviewEntity;
    export let customerName: string;
    export let translationEnabled: boolean;
    export let translsatedContent: {
        title: string | null;
        body: string;
        reply: string | null;
    };
    export let showTranslation: boolean;
    export let isTranslationLoading: boolean;
    export let showTitle: boolean;
    export let showProducts: boolean;
    export let votesEnabled: boolean;
    export let reviewVotes: VotesState | undefined;
    export let isVerifiedBuyerLabelEnabled: boolean;
    export let isVerifiedBuyerByMerchantLabelEnabled: boolean;
    export let isImportedEnabled: boolean;
    export let isIncentivizedEnabled: boolean;
    export let currentProductId: number | null | undefined;
    export let isReviewEditingEnabled: boolean;
</script>

<ReviewItemFrame>
    <svelte:fragment slot="author">
        <span class="gw-rv-item__author">
            {customerName}
        </span>
    </svelte:fragment>

    <svelte:fragment slot="meta">
        {#if (isVerifiedBuyerLabelEnabled && review.customer?.isVerifiedBuyer) || (isVerifiedBuyerByMerchantLabelEnabled && review.customer?.isVerifiedBuyerByMerchant)}
            <VerifiedBuyer
                {namespace}
                isVerifiedBuyer={review.customer.isVerifiedBuyer}
                isVerifiedBuyerByMerchant={review.customer
                    .isVerifiedBuyerByMerchant}
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="see-translation-btn">
        {#if translationEnabled && review.isPublished}
            <ReviewsTranslateButton
                {namespace}
                {review}
                {showTranslation}
                {isTranslationLoading}
                on:seeTranslation
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="published">
        {#if (isImportedEnabled && review.isImported) || (isIncentivizedEnabled && review.isIncentivized)}
            <div class="gw-rv-item__badges">
                {#if isImportedEnabled && review.isImported}
                    <ImportedReview {namespace} />
                {/if}
                {#if isIncentivizedEnabled && review.isIncentivized}
                    <Incentivized {namespace} />
                {/if}
            </div>
        {/if}

        {#if !review.isPublished}
            <Badge icon={ClockIcon} size="lg" variant="flat" transparent>
                <T {namespace} translationKey="under_review" />
            </Badge>
        {/if}
        {#if isReviewEditingEnabled && review.isPublished}
            <ReviewsEditButton {namespace} on:editReview />
        {/if}
    </svelte:fragment>

    <ReviewRatingStars
        slot="rating"
        rating={review.rating}
        width="1.4em"
        height="1.4em"
    />

    <svelte:fragment slot="date">
        <div class="gw-rv-item__date">
            {getHumanReadableDate(review.createdAt)}
        </div>
    </svelte:fragment>

    <svelte:fragment slot="title">
        {#if showTitle && review.title}
            <span class="gw-rv-item__title">
                {translsatedContent.title || ""}
            </span>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="text">
        <ViewMore text={translsatedContent.body || ""} class="gw-rv-item__text">
            <svelte:fragment slot="view_more_text">
                <T {namespace} translationKey="view_more" />
            </svelte:fragment>
            <svelte:fragment slot="view_less_text">
                <T {namespace} translationKey="view_less" />
            </svelte:fragment>
        </ViewMore>
    </svelte:fragment>

    <svelte:fragment slot="customer-attributes">
        {#if review.customerAttributeAnswers.length > 0}
            <div class="gw-rv-item__attributes">
                <AttributeAnswers
                    attributeAnswers={review.customerAttributeAnswers}
                />
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="images">
        {#if review.images}
            <ReviewImageList
                on:openReview
                images={review.images}
                reviewTitle={translsatedContent.title || ""}
            />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="product-attributes">
        {#if review.productAttributeAnswers.length > 0}
            <div class="gw-rv-item__attributes">
                <AttributeAnswers
                    attributeAnswers={review.productAttributeAnswers}
                />
            </div>
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="vote">
        {#if votesEnabled && reviewVotes}
            <ToggleVote {reviewVotes} on:toggleVote />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="reply">
        {#if review.reply && review.reply.status === REVIEWS_REPLY_STATUS.PUBLIC}
            <ReviewReply {namespace} reply={translsatedContent.reply || ""} />
        {/if}
    </svelte:fragment>

    <svelte:fragment slot="footer">
        {#if showProducts && review.product?.id}
            {#if review.product?.isDraft || review.product?.isDeleted}
                <div class="gw-rv-item__footer-wrapper">
                    <ReviewsUnavailableProduct
                        productId={review.product.id}
                        token={review.product?.unavailableProductToken || ""}
                        on:productReady
                    />
                </div>
            {:else if !currentProductId || review.product.id !== currentProductId}
                <div class="gw-rv-item__footer-wrapper">
                    <ReviewsProduct
                        productId={review.product.id}
                        on:productReady
                    />
                </div>
            {/if}
        {/if}
    </svelte:fragment>
</ReviewItemFrame>

<style lang="postcss">
    .gw-rv-item {
        &__author {
            font-size: 1em;
            font-weight: 600;
        }

        &__badges {
            display: flex;
            align-items: center;
            flex: 1;
            gap: 0.5em;
        }

        &__title {
            width: 100%;
            display: inline-block;
            font-weight: 600;
            font-size: 0.875em;
        }

        :global(&__text) {
            font-size: 0.875em;

            word-break: break-word;
            overflow-wrap: break-word;
            margin: 0;
        }

        &__attributes {
            margin: 1em 0 0;
        }

        &__date {
            opacity: 0.7;
            font-size: 0.75em;
            line-height: 1em;
        }

        &__footer-wrapper {
            margin-top: 0.75em;
        }
    }
</style>
